// Account closure helpers shared by CloseAccountModal and the sign-in paths.
//
// How a closed account is refused at sign-in: the pool's pre-token-generation
// trigger (customClaims, with infra/lambda/accountClosure/trigger/
// closedAccountCheck.js) throws ACCOUNT_CLOSED:<deletion date> for an account
// with a closure record. Cognito runs that trigger every way in — password,
// Google / Apple / Facebook through the Hosted UI, a token refresh — and
// passes the text back:
//
//   "PreTokenGeneration failed with error ACCOUNT_CLOSED:2026-12-12."
//
// So "User is disabled." (password) / "User is not enabled" (Hosted UI) only
// ever means a suspension or a ban, and the app needs no lookup, tag or
// browser memory to tell the two apart.

export const GRACE_DAYS = 60

export function formatDeletionDate(deleteAt) {
    // A bare YYYY-MM-DD (the trigger's date) is a calendar date: parsed as UTC
    // midnight it would show the day before anywhere west of Greenwich.
    const day = typeof deleteAt === 'string' && deleteAt.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    const d = day ? new Date(+day[1], +day[2] - 1, +day[3]) : deleteAt ? new Date(deleteAt) : null
    if (!d || isNaN(d)) return ''
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

/**
 * What a failed sign-in says about the account: { closed, deleteAt },
 * { disabled }, or null for any other failure. Hosted UI redirects carry the
 * text URL-encoded ("User+is+not+enabled", "ACCOUNT_CLOSED%3A2026-12-12").
 */
export function signInBlock(message) {
    if (typeof message !== 'string' || !message) return null
    let text = message.replace(/\+/g, ' ')
    try { text = decodeURIComponent(text) } catch (e) { /* not encoded */ }
    const closed = text.match(/ACCOUNT_CLOSED(?::(\d{4}-\d{2}-\d{2}))?/)
    if (closed) return { closed: true, deleteAt: closed[1] || null }
    if (/^\s*User is (disabled|not enabled)/i.test(text)) return { disabled: true }
    return null
}

/** The toast for a blocked sign-in (signInBlock's result). */
export function signInBlockMessage(block) {
    if (block && block.closed) {
        const when = formatDeletionDate(block.deleteAt)
        return `This account was closed${when ? `<br>and will be permanently deleted on ${when}` : ''}.<br><br>Please contact us if you didn't close it.<br>`
    }
    // The bracket is for accounts closed while closures still disabled the
    // user (CLOSURE_BLOCK=disable, the default until customClaims checks).
    return 'This account is disabled, usually as a precaution<br>due to unusual activity on the account.<br><br>Please contact us if you feel this action was erroneous.<br>(If you closed this account yourself, it will be<br>permanently deleted 60 days after closing.)<br>'
}

// One failed Hosted UI sign-in reaches both main.js (the Amplify Hub event)
// and Nav (the redirect's query string); whichever sees it first announces it.
let announcedAt = 0
export function claimBlockAnnouncement() {
    const now = Date.now()
    if (now - announcedAt < 10000) return false
    announcedAt = now
    return true
}
